import { useCallback, useEffect, useState } from 'react'
import { onboardWhatsappMeta, listWhatsappMetaAccounts } from '../../db/api.js'
import { useApp } from '../../context/AppContext.jsx'
import {
  loadFacebookSdk,
  launchEmbeddedSignup,
  isMetaSignupConfigured,
} from '../../lib/metaSignup.js'

/**
 * Onboarding do WhatsApp Oficial (Meta Cloud API) via Embedded Signup CoEx.
 *
 * Página standalone (/whatsapp/meta). O fluxo de SDK/Embedded Signup vive em
 * lib/metaSignup.js, compartilhado com o modal de integração do Perfil.
 */
export default function MetaConnect() {
  const { selectedClient } = useApp()
  const selectedClientId = selectedClient?.id || null

  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)

  const reloadAccounts = useCallback(async () => {
    try {
      const rows = await listWhatsappMetaAccounts()
      setAccounts(rows)
    } catch (e) {
      setError(e.message)
    }
  }, [])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        await loadFacebookSdk()
        if (alive) await reloadAccounts()
      } catch (e) {
        if (alive) setError(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [reloadAccounts])

  const launchSignup = useCallback(async () => {
    if (!selectedClientId) {
      setError('Selecione um cliente antes de conectar o WhatsApp.')
      return
    }
    if (!isMetaSignupConfigured()) {
      setError('VITE_META_APP_ID / VITE_META_CONFIG_ID não configurados.')
      return
    }
    setError(null)
    setInfo(null)
    setBusy(true)
    try {
      const { code, wabaId, phoneNumberId } = await launchEmbeddedSignup()
      const acc = await onboardWhatsappMeta({
        clientId: selectedClientId,
        code,
        wabaId,
        phoneNumberId,
      })
      setInfo(`Número conectado: ${acc.display_phone_number || acc.phone_number_id}.`)
      await reloadAccounts()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [selectedClientId, reloadAccounts])

  if (loading) return <p>Carregando…</p>

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <h2>WhatsApp Oficial (Meta)</h2>
      <p style={{ fontSize: 14, color: '#666' }}>
        Conecte o número que já usa no WhatsApp Business App. Ele continua
        funcionando no celular e passa a enviar/receber também pela API oficial.
      </p>

      {error && <p style={{ color: 'crimson' }}>{error}</p>}
      {info && <p style={{ color: 'green' }}>{info}</p>}

      <button onClick={launchSignup} disabled={busy}>
        {busy ? 'Conectando…' : 'Conectar número via Meta'}
      </button>

      <h3 style={{ marginTop: 24 }}>Números conectados</h3>
      {accounts.length === 0 ? (
        <p style={{ color: '#888' }}>Nenhum número conectado ainda.</p>
      ) : (
        <ul>
          {accounts.map((a) => (
            <li key={a.id}>
              <strong>{a.display_phone_number || a.phone_number_id}</strong>
              {a.verified_name ? ` — ${a.verified_name}` : ''} ({a.status})
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
